import { DEFAULT_LOCALE, LOCALES, type SupportedLocale } from "./constants";
import { readStoredLocale } from "./storage";

const localeMap = new Map<string, SupportedLocale>([
  ["pt", "pt-BR"],
  ["pt-br", "pt-BR"],
  ["en", "en-US"],
  ["en-us", "en-US"],
  ["es", "es-ES"],
  ["es-es", "es-ES"],
]);

export function normalizeLocale(input?: string | null): SupportedLocale {
  if (!input) {
    return DEFAULT_LOCALE;
  }

  if ((LOCALES as readonly string[]).includes(input)) {
    return input as SupportedLocale;
  }

  const normalized = input.trim().toLowerCase().replace("_", "-");

  return localeMap.get(normalized) ?? localeMap.get(normalized.split("-")[0] ?? "") ?? DEFAULT_LOCALE;
}

export function detectBrowserLocale(): SupportedLocale {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }

  const candidates = Array.isArray(window.navigator.languages) && window.navigator.languages.length > 0
    ? window.navigator.languages
    : [window.navigator.language];

  for (const locale of candidates) {
    const normalized = normalizeLocale(locale);
    if (normalized) {
      return normalized;
    }
  }

  return DEFAULT_LOCALE;
}

export function detectLocale(candidates: readonly string[]): SupportedLocale {
  for (const locale of candidates) {
    const normalized = normalizeLocale(locale);
    if (normalized) {
      return normalized;
    }
  }

  return DEFAULT_LOCALE;
}

/**
 * Idioma efetivo fora do React: preferência salva na página (mesma chave do I18n),
 * senão o melhor match a partir do navegador. Usado em chamadas à API sem sessão.
 */
export function getClientLocaleHint(): SupportedLocale {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }
  return readStoredLocale() ?? detectBrowserLocale();
}

/** Valor de `Accept-Language` que reflete o locale do app (API usa na resolução de e-mails). */
export function acceptLanguageHeaderForLocale(locale: SupportedLocale): string {
  const short = locale.split("-")[0] ?? locale;
  return `${locale},${short};q=0.9,en;q=0.6,pt;q=0.5,es;q=0.5`;
}
